<template>
  <div class="space-y-4 sm:space-y-6">
    <!-- Welcome Section -->
    <div class="card mb-6">
      <div class="card-body">
        <div
          class="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6"
        >
          <div
            class="h-16 w-16 sm:h-20 sm:w-20 rounded-full overflow-hidden bg-[#0275b1] ring-4 ring-[#ffd62e] flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-md transform transition-transform duration-300 hover:scale-105"
          >
            <img
              v-if="userImage"
              :src="userImage"
              :alt="userName"
              class="w-full h-full object-cover"
            />
            <span v-else>{{ userInitials }}</span>
          </div>
          <div class="text-center sm:text-left">
            <h1 class="text-xl sm:text-2xl font-bold text-[#0b6623] dark:text-emerald-400">
              Welcome, {{ userName }}!
            </h1>
            <p class="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">
              {{ userEmail }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Register employee Modal -->
  <RegisterEmployeeModal
    :is-visible="isRegisterModalVisible"
    @close="isRegisterModalVisible = false"
    @success="handleRegistrationSuccess"
  />

  <!-- View Staff Profile Modal (Sprint 2 Full Profile) -->
  <ViewStaffProfileModal
    :is-visible="isViewProfileModalVisible"
    :employee="selectedEmployeeForProfile"
    @close="handleViewProfileClose"
    @edit="handleProfileOpenEdit"
    @manage-documents="handleProfileOpenDocuments"
    @upload-documents="handleProfileOpenUpload"
    @view-application="openApplicationDetails"
  />

  <!-- Edit Staff Profile Modal (Sprint 2 Full Profile) -->
  <EditStaffProfileModal
    :is-visible="isEditProfileModalVisible"
    :employee="selectedEmployeeForEdit"
    @close="isEditProfileModalVisible = false"
    @success="handleProfileEditSuccess"
  />

  <!-- View & Manage Staff Documents Modal (Sprint 2 Documents) -->
  <ViewStaffDocumentsModal
    ref="staffDocumentsModalRef"
    :is-visible="isDocumentsModalVisible"
    :employee="selectedEmployeeForDocuments"
    :initial-category="initialDocumentsCategory"
    @close="handleDocumentsModalClose"
    @upload="openUploadDocuments"
    @updated="handleDocumentsUpdated"
  />

  <!-- Upload Staff Documents Modal (Sprint 2 Batch/Single Upload) -->
  <UploadStaffDocumentsModal
    :is-visible="isUploadModalVisible"
    :employee="selectedEmployeeForUpload"
    @close="isUploadModalVisible = false"
    @success="handleUploadSuccess"
  />

  <!-- Delete employee Modal -->
  <DeleteEmployeeModal
    :is-visible="isDeleteModalVisible"
    :employee="selectedEmployeeForDelete"
    @close="isDeleteModalVisible = false"
    @success="handleDeleteSuccess"
  />

  <!-- View Application Details Modal (System Approved Staff Application Form) -->
  <ViewApplicationDetails
    :is-visible="isApplicationDetailsModalVisible"
    :application="selectedApplicationDetails"
    @close="handleCloseApplicationDetails"
    @documents-updated="handleApplicationDocumentsUpdated"
  />

  <!-- Loading State -->
  <div v-if="isLoading" class="mt-6 flex flex-col justify-center items-center py-12">
    <vue-loaders
      name="line-scale"
      :color="isDarkMode ? '#34d399' : '#0b6623'"
      scale="1.2"
      class="mb-4"
    />
    <span class="text-gray-900 dark:text-white">Loading staff...</span>
  </div>

  <!-- Error State -->
  <div
    v-else-if="error"
    class="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
  >
    <div class="flex items-center">
      <AlertCircle class="h-5 w-5 text-red-500 mr-2" />
      <span class="text-red-700 dark:text-red-300 font-medium">{{ error }}</span>
    </div>
    <button
      @click="fetchEmployees"
      class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
    >
      Try again
    </button>
  </div>

  <!-- Main Content -->
  <div v-else class="space-y-4 sm:space-y-6">
    <!-- Staff Data Table -->
    <DataTable
      :data="employees"
      :columns="tableColumns"
      :loading="isLoading"
      :sortable-columns="sortableColumns"
      :mobile-columns="mobileColumns"
      item-key="user_details_id"
      item-label="staff"
      search-placeholder="Search by name, email..."
      :summary-icon="Users"
      :show-search="true"
      :show-filters="true"
      :show-pagination="true"
      :show-summary="true"
      :disable-client-filter="true"
      :items-per-page="10"
      :items-per-page-options="[5, 10, 25, 50]"
      table-id="staff-table"
      @search="handleSearch"
      @sort="handleSort"
      @filter="handleFilter"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
    >
      <!-- Custom action buttons -->
      <template #actions>
        <div class="flex items-center gap-2">
          <router-link
            to="/applications"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 flex items-center gap-1.5 transition shadow-sm"
            title="View approved applications pending staff account creation"
          >
            <FileText class="h-3.5 w-3.5 text-secondary dark:text-secondary-dark-text" />
            <span class="hidden sm:inline">Approved Applicants</span>
          </router-link>

          <button @click="registerNewUser" class="btn btn-primary btn-sm flex items-center gap-2">
            <Plus class="h-4 w-4" />
            <span class="hidden sm:inline">Register Staff</span>
          </button>
        </div>
      </template>

      <!-- Custom filters -->
      <template #filters="{ updateFilter }">
        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            v-model="statusFilter"
            @change="updateFilter('status', statusFilter)"
            class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <!-- Work State Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Work State
          </label>
          <select
            v-model="workStateFilter"
            @change="updateFilter('workState', workStateFilter)"
            class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
          >
            <option value="">All States</option>
            <option value="Maryland">Maryland</option>
            <option value="DC">DC</option>
            <option value="Both">Both (MD & DC)</option>
          </select>
        </div>

        <!-- Service Population Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client Group
          </label>
          <select
            v-model="servicePopulationFilter"
            @change="updateFilter('servicePopulation', servicePopulationFilter)"
            class="block w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200 shadow-sm hover:shadow-md"
          >
            <option value="">All Client Groups</option>
            <option value="Adults">Adults</option>
            <option value="Kids">Kids</option>
            <option value="Both">Both (Adults & Kids)</option>
          </select>
        </div>
      </template>

      <!-- Custom summary -->
      <template #summary="{ totalItems }">
        <div v-if="totalItems > 0" class="flex items-center gap-4 text-sm">
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 bg-green-500 rounded-full"></div>
            <span class="text-gray-600 dark:text-gray-400">
              <span class="font-medium text-green-600 dark:text-green-400">{{
                activeEmployees
              }}</span>
              active
            </span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-2 h-2 bg-red-500 rounded-full"></div>
            <span class="text-gray-600 dark:text-gray-400">
              <span class="font-medium text-red-600 dark:text-red-400">{{
                inactiveEmployees
              }}</span>
              inactive
            </span>
          </div>
        </div>
      </template>


      <!-- Email column cell -->
      <template #cell-email="{ value }">
        <span class="break-all">{{ value }}</span>
      </template>

      <template #mobile-email="{ value }">
        <span class="break-all">{{ value }}</span>
      </template>

      <!-- Indicators column cell (Desktop - Plain text per row) -->
      <template #cell-indicators="{ item }">
        <div class="flex flex-col items-start justify-center space-y-1 text-xs py-0.5 whitespace-nowrap">
          <!-- Row 1: State -->
          <div class="flex items-center space-x-1.5">
            <span class="text-gray-400 dark:text-gray-500 font-medium">State:</span>
            <span
              v-if="item.work_state"
              class="font-semibold"
              :class="[
                item.work_state === 'Maryland'
                  ? 'text-blue-600 dark:text-blue-400'
                  : item.work_state === 'DC'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-indigo-600 dark:text-indigo-400'
              ]"
            >
              {{ item.work_state === 'Both' ? 'MD & DC' : item.work_state }}
            </span>
            <span v-else class="text-gray-400 dark:text-gray-500 italic">None</span>
          </div>

          <!-- Row 2: Group -->
          <div class="flex items-center space-x-1.5">
            <span class="text-gray-400 dark:text-gray-500 font-medium">Group:</span>
            <span
              v-if="item.service_population"
              class="font-semibold"
              :class="[
                item.service_population === 'Adults'
                  ? 'text-amber-700 dark:text-amber-400'
                  : item.service_population === 'Kids'
                  ? 'text-pink-600 dark:text-pink-400'
                  : 'text-teal-600 dark:text-teal-400'
              ]"
            >
              {{ item.service_population === 'Both' ? 'Adults & Kids' : item.service_population }}
            </span>
            <span v-else class="text-gray-400 dark:text-gray-500 italic">None</span>
          </div>
        </div>
      </template>

      <!-- Indicators column cell (Mobile - Plain text per row) -->
      <template #mobile-indicators="{ item }">
        <div class="flex flex-col items-end justify-center space-y-1 text-xs">
          <div class="flex items-center space-x-1.5">
            <span class="text-gray-400 dark:text-gray-500 font-medium">State:</span>
            <span
              v-if="item.work_state"
              class="font-semibold"
              :class="[
                item.work_state === 'Maryland'
                  ? 'text-blue-600 dark:text-blue-400'
                  : item.work_state === 'DC'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-indigo-600 dark:text-indigo-400'
              ]"
            >
              {{ item.work_state === 'Both' ? 'MD & DC' : item.work_state }}
            </span>
            <span v-else class="text-gray-400 dark:text-gray-500 italic">None</span>
          </div>

          <div class="flex items-center space-x-1.5">
            <span class="text-gray-400 dark:text-gray-500 font-medium">Group:</span>
            <span
              v-if="item.service_population"
              class="font-semibold"
              :class="[
                item.service_population === 'Adults'
                  ? 'text-amber-700 dark:text-amber-400'
                  : item.service_population === 'Kids'
                  ? 'text-pink-600 dark:text-pink-400'
                  : 'text-teal-600 dark:text-teal-400'
              ]"
            >
              {{ item.service_population === 'Both' ? 'Adults & Kids' : item.service_population }}
            </span>
            <span v-else class="text-gray-400 dark:text-gray-500 italic">None</span>
          </div>
        </div>
      </template>


      <!-- Documents column cell -->
      <template #cell-documents="{ item }">
        <button
          type="button"
          @click="openStaffDocuments(item)"
          class="inline-flex min-w-[132px] items-center justify-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold shadow-xs transition hover:bg-purple-50 dark:border-purple-800 dark:bg-gray-800 dark:hover:bg-purple-950/30"
          :class="[
            (item.total_documents || 0) > 0
              ? 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800'
              : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700'
          ]"
          title="Click to view/manage documents"
        >
          <span class="text-gray-600 dark:text-gray-300">{{ item.total_documents || 0 }} current</span>
          <span v-if="item.is_former_employee" class="border-l border-purple-200 pl-1.5 text-amber-700 dark:border-purple-800 dark:text-amber-300">
            {{ item.former_employee?.total_files || 0 }} archived
          </span>
        </button>
      </template>

      <template #mobile-documents="{ item }">
        <button
          type="button"
          @click="openStaffDocuments(item)"
          class="inline-flex min-w-[122px] items-center justify-center gap-2 rounded-lg border border-purple-200 bg-white px-2.5 py-1.5 text-xs font-semibold shadow-xs dark:border-purple-800 dark:bg-gray-800"
        >
          <span class="text-gray-600 dark:text-gray-300">{{ item.total_documents || 0 }} current</span>
          <span v-if="item.is_former_employee" class="border-l border-purple-200 pl-1.5 text-amber-700 dark:border-purple-800 dark:text-amber-300">
            {{ item.former_employee?.total_files || 0 }} archived
          </span>
        </button>
      </template>

      <!-- Middle Name column cell -->
      <template #cell-middle_name="{ value }">
        <span :class="value ? '' : 'text-gray-400 dark:text-gray-500 italic'">
          {{ value || 'Not stated' }}
        </span>
      </template>

      <template #mobile-middle_name="{ value }">
        <span :class="value ? '' : 'text-gray-400 dark:text-gray-500 italic'">
          {{ value || 'Not stated' }}
        </span>
      </template>

      <!-- Mobile Status column cell -->
      <template #mobile-status="{ item }">
        <div class="flex items-center justify-end gap-2">
          <span
            :class="[
              'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
              item.status === 'Active'
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            ]"
          >
            {{ item.status }}
          </span>
          <span
            v-if="item.is_former_employee"
            class="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            title="This account is linked to a former employee archive record."
          >
            <Archive class="h-3.5 w-3.5" />
          </span>
        </div>
      </template>

      <!-- Profile column cell -->
      <template #cell-profile="{ item }">
        <div class="flex items-center">
          <div
            class="h-10 w-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center"
          >
            <img
              v-if="item.user_image"
              :src="item.user_image"
              :alt="`${item.first_name} ${item.last_name}`"
              class="h-full w-full object-cover"
              @error="handleImageError"
            />
            <span v-else class="text-gray-500 text-sm">
              {{ item.first_name?.charAt(0) || '' }}{{ item.last_name?.charAt(0) || '' }}
            </span>
          </div>
        </div>
      </template>

      <!-- Status column cell -->
      <template #cell-status="{ item }">
        <div class="flex items-center justify-center gap-2">
          <span
            :class="[
              'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
              item.status === 'Active'
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            ]"
          >
            {{ item.status }}
          </span>
          <span
            v-if="item.is_former_employee"
            class="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            :title="`Former employee record linked${item.former_employee?.date_separated ? `; separated ${item.former_employee.date_separated}` : ''}${item.former_employee?.reason_for_leaving ? `; ${item.former_employee.reason_for_leaving}` : ''}.`"
          >
            <Archive class="h-3.5 w-3.5" />
          </span>
        </div>
      </template>

      <!-- Actions column -->
      <template #row-actions="{ item }">
        <div class="flex justify-center items-center space-x-1 sm:space-x-1.5">
          <!-- Status Toggle Icon Button -->
          <button
            :class="[
              'p-1.5 rounded-lg transition duration-200 transform',
              item.status === 'Active'
                ? 'text-secondary dark:text-secondary-dark dark:hover:text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800'
                : 'text-primary dark:text-primary-dark-text hover:text-gray-900 dark:hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
            ]"
            @click="toggleUserStatus(item)"
            :title="item.status === 'Active' ? 'Deactivate User' : 'Activate User'"
          >
            <LockOpen v-if="item.status === 'Active'" class="h-4 w-4" />
            <Lock v-else class="h-4 w-4" />
          </button>

          <!-- View Profile Button -->
          <button
            class="p-1.5 rounded-lg text-sky-600 hover:text-sky-800 hover:bg-sky-50 dark:text-sky-400 dark:hover:text-sky-200 dark:hover:bg-sky-950/30 transition"
            @click="openViewProfile(item)"
            title="View Full Staff Profile"
          >
            <Eye class="h-4 w-4" />
          </button>

          <!-- View Approved Application Button (System Approved Applicant Indicator & Action) -->
          <button
            v-if="item.has_application && item.application_id"
            class="p-1.5 rounded-lg text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:text-emerald-200 dark:hover:bg-emerald-950/30 transition relative"
            @click="openApplicationDetails(item)"
            :title="`Approved via Application System (App #${item.application_id}) - View Full Application Details`"
          >
            <FileCheck class="h-4 w-4" />
          </button>

          <!-- View Documents Button -->
          <button
            class="p-1.5 rounded-lg text-purple-600 hover:text-purple-800 hover:bg-purple-50 dark:text-purple-400 dark:hover:text-purple-200 dark:hover:bg-purple-950/30 transition"
            @click="openStaffDocuments(item)"
            title="Manage Credentials & Documents"
          >
            <FolderLock class="h-4 w-4" />
          </button>

          <!-- Edit Profile Button -->
          <button
            class="p-1.5 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition"
            @click="openEditProfile(item)"
            title="Edit Staff Profile"
          >
            <Edit3 class="h-4 w-4" />
          </button>

          <!-- Delete Icon Button -->
          <button
            class="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 transition"
            @click="deleteUser(item)"
            title="Delete Staff"
          >
            <Trash2 class="h-4 w-4" />
          </button>
        </div>
      </template>

      <!-- Mobile actions -->
      <template #mobile-actions="{ item }">
        <div class="flex flex-wrap justify-end gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-700/60">
          <!-- Status Toggle -->
          <button
            :class="[
              'transition duration-200 transform flex items-center gap-1 px-2 py-1 rounded text-xs',
              item.status === 'Active'
                ? 'text-secondary dark:text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
                : 'text-primary dark:text-primary-dark hover:bg-gray-100 dark:hover:bg-gray-700'
            ]"
            @click="toggleUserStatus(item)"
            :title="item.status === 'Active' ? 'Deactivate User' : 'Activate User'"
          >
            <LockOpen v-if="item.status === 'Active'" class="h-3.5 w-3.5" />
            <Lock v-else class="h-3.5 w-3.5" />
            <span>{{ item.status === 'Active' ? 'Deactivate' : 'Activate' }}</span>
          </button>

          <!-- View Profile -->
          <button
            class="text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-200 transition flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-sky-50 dark:hover:bg-sky-950/30"
            @click="openViewProfile(item)"
            title="View Profile"
          >
            <Eye class="h-3.5 w-3.5" />
            <span>Profile</span>
          </button>

          <!-- View Approved Application (Mobile) -->
          <button
            v-if="item.has_application && item.application_id"
            class="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 transition flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
            @click="openApplicationDetails(item)"
            :title="`Approved via Application System (App #${item.application_id}) - View Details`"
          >
            <FileCheck class="h-3.5 w-3.5" />
            <span>App</span>
          </button>

          <!-- Documents -->
          <button
            class="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200 transition flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-purple-50 dark:hover:bg-purple-950/30"
            @click="openStaffDocuments(item)"
            title="Documents"
          >
            <FolderLock class="h-3.5 w-3.5" />
            <span>Docs</span>
          </button>

          <!-- Edit Button -->
          <button
            class="text-blue-500 hover:text-blue-700 transition flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-blue-50 dark:hover:bg-blue-950/30"
            @click="openEditProfile(item)"
            title="Edit Staff"
          >
            <Edit3 class="h-3.5 w-3.5" />
            <span>Edit</span>
          </button>

          <!-- Delete Button -->
          <button
            class="text-red-500 hover:text-red-700 transition flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-red-50 dark:hover:bg-red-950/30"
            @click="deleteUser(item)"
            title="Delete Staff"
          >
            <Trash2 class="h-3.5 w-3.5" />
            <span>Delete</span>
          </button>
        </div>
      </template>

      <!-- Empty actions -->
      <template #empty-actions>
        <button @click="registerNewUser" class="btn btn-primary btn-md">Register Staff</button>
      </template>
    </DataTable>
  </div>
  <!-- End of main content wrapper -->
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useTitle } from '@/composables/ui/useTitle'
import { useUser } from '@/composables/auth/useUser'
import { useEmployees } from '@/composables/employee/useEmployees'
import { useEmployeeStore } from '@/stores/employee'
import { useThemeStore } from '@/stores/theme'
import { employeeService } from '@/services/user'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import type { ApplicationDetail } from '@/types/application'
import { applicationService } from '@/services/applicationService'
import RegisterEmployeeModal from '@/components/modals/RegisterEmployee.vue'
import ViewStaffProfileModal from '@/components/modals/ViewStaffProfile.vue'
import EditStaffProfileModal from '@/components/modals/EditStaffProfile.vue'
import ViewStaffDocumentsModal from '@/components/modals/ViewStaffDocuments.vue'
import UploadStaffDocumentsModal from '@/components/modals/UploadStaffDocuments.vue'
import DeleteEmployeeModal from '@/components/modals/DeleteEmployee.vue'
import ViewApplicationDetails from '@/components/modals/ViewApplicationDetails.vue'
import DataTable from '@/components/common/DataTable.vue'
import { 
  AlertCircle,
  Archive,
  Plus,
  Lock,
  LockOpen,
  Edit3,
  Trash2,
  Users,
  FileText,
  FileCheck,
  FolderLock,
  Eye,
} from 'lucide-vue-next'

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Jeducare')
useTitle(`Staff - ${appTitle.value}`)

const route = useRoute()
const initialDocumentsCategory = ref<string>('all')

const { userInitials, userName, userEmail, userImage } = useUser()

// Toast service for notifications
const toast = useToastService()

// Theme store for dark mode detection
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// employee management
const {
  employees,
  isLoading,
  error,
  fetchEmployees,
  setSearch,
  setStatusFilter,
  setWorkStateFilter,
  setServicePopulationFilter,
  setPagination,
  activeEmployees,
  inactiveEmployees,
  statusFilter: storedStatusFilter,
  workStateFilter: storedWorkStateFilter,
  servicePopulationFilter: storedServicePopulationFilter
} = useEmployees()

// Modal state
const isRegisterModalVisible = ref(false)
const isDeleteModalVisible = ref(false)
const isViewProfileModalVisible = ref(false)
const isEditProfileModalVisible = ref(false)
const isDocumentsModalVisible = ref(false)
const isUploadModalVisible = ref(false)

// Application Details Modal (for staff approved through application system)
const isApplicationDetailsModalVisible = ref(false)
const selectedApplicationDetails = ref<ApplicationDetail | null>(null)
const isLoadingApplicationDetails = ref(false)

const selectedEmployeeForProfile = ref<employee | null>(null)
const selectedEmployeeForEdit = ref<employee | null>(null)
const selectedEmployeeForDocuments = ref<employee | null>(null)
const selectedEmployeeForUpload = ref<employee | null>(null)
const selectedEmployeeForDelete = ref<employee | null>(null)

const staffDocumentsModalRef = ref<any>(null)

// Local filter state - initialize from stored values
const statusFilter = ref(storedStatusFilter.value)
const workStateFilter = ref(storedWorkStateFilter.value || '')
const servicePopulationFilter = ref(storedServicePopulationFilter.value || '')

// Table configuration
const tableColumns = [
  { key: 'profile', label: 'Profile', sortable: false, width: '70px' },
  { key: 'first_name', label: 'First Name', sortable: true },
  { key: 'last_name', label: 'Last Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'indicators', label: 'State & Group', sortable: false, width: '150px' },
  { key: 'documents', label: 'Documents', sortable: false, align: 'center' as const, width: '120px' },
  { key: 'status', label: 'Status', sortable: true, align: 'center' as const, width: '90px' }
]

const sortableColumns = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' }
]

const mobileColumns = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'indicators', label: 'State & Group' },
  { key: 'documents', label: 'Documents' },
  { key: 'status', label: 'Status' }
]

// DataTable event handlers
const handleSearch = (query: string) => {
  setSearch(query)
}

const employeeStore = useEmployeeStore()

const handleSort = (sort: { field: string; direction: 'asc' | 'desc' }) => {
  employeeStore.setFilters({ sortBy: sort.field, sortDirection: sort.direction })
}

const handleFilter = (filters: Record<string, any>) => {
  if (filters.status !== undefined) {
    statusFilter.value = filters.status
    setStatusFilter(filters.status as 'Active' | 'Inactive' | '')
  }
  if (filters.workState !== undefined) {
    workStateFilter.value = filters.workState
    setWorkStateFilter(filters.workState)
  }
  if (filters.servicePopulation !== undefined) {
    servicePopulationFilter.value = filters.servicePopulation
    setServicePopulationFilter(filters.servicePopulation)
  }
}

const handlePageChange = (page: number) => {
  setPagination({ page })
}

const handleItemsPerPageChange = (itemsPerPage: number) => {
  setPagination({ itemsPerPage })
}

// Helper functions
const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  if (target) {
    target.style.display = 'none'
  }
}

// Modal handlers
const handleRegistrationSuccess = () => {
  fetchEmployees()
}

const handleDeleteSuccess = () => {
  setSearch('')
  fetchEmployees()
}

// Staff Action Handlers
const registerNewUser = () => {
  isRegisterModalVisible.value = true
}

const toggleUserStatus = async (user: any) => {
  try {
    const newStatus = user.status === 'Active' ? 0 : 1
    const action = newStatus === 1 ? 'activated' : 'deactivated'

    await employeeService.updateUserStatus(user.user_details_id, newStatus)

    user.status = newStatus === 1 ? 'Active' : 'Inactive'
    toast.success(`${user.first_name} ${user.last_name} has been ${action}.`)
  } catch (error: any) {
    console.error('Error toggling user status:', error)
    toast.error('Failed to update user status. Please try again.')
  }
}

// View Profile
const openViewProfile = (user: any) => {
  selectedEmployeeForProfile.value = user
  isViewProfileModalVisible.value = true
}

const handleViewProfileClose = () => {
  isViewProfileModalVisible.value = false
}

// Edit Profile
const openEditProfile = (user: any) => {
  selectedEmployeeForEdit.value = user
  isEditProfileModalVisible.value = true
}

const handleProfileOpenEdit = (user: any) => {
  isViewProfileModalVisible.value = false
  openEditProfile(user)
}

const handleProfileEditSuccess = (updatedEmp: employee) => {
  fetchEmployees()
  if (selectedEmployeeForProfile.value && selectedEmployeeForProfile.value.user_details_id === updatedEmp.user_details_id) {
    selectedEmployeeForProfile.value = { ...selectedEmployeeForProfile.value, ...updatedEmp }
  }
}

// Manage Documents
const openStaffDocuments = (user: any, category = 'all') => {
  selectedEmployeeForDocuments.value = user
  initialDocumentsCategory.value = category
  isDocumentsModalVisible.value = true
}

const handleDocumentsModalClose = () => {
  isDocumentsModalVisible.value = false
}

const handleProfileOpenDocuments = (user: any) => {
  isViewProfileModalVisible.value = false
  openStaffDocuments(user)
}

const handleDocumentsUpdated = (count: number) => {
  if (selectedEmployeeForDocuments.value) {
    selectedEmployeeForDocuments.value.total_documents = count
  }
  if (selectedEmployeeForProfile.value) {
    selectedEmployeeForProfile.value.total_documents = count
  }
  // Also update in the table
  const found = employees.value.find(e => e.user_details_id === selectedEmployeeForDocuments.value?.user_details_id)
  if (found) {
    found.total_documents = count
  }
}

// Upload Documents
const openUploadDocuments = (user: any) => {
  selectedEmployeeForUpload.value = user
  isUploadModalVisible.value = true
}

const handleProfileOpenUpload = (user: any) => {
  isViewProfileModalVisible.value = false
  openUploadDocuments(user)
}

const handleUploadSuccess = () => {
  fetchEmployees()
  if (isDocumentsModalVisible.value && staffDocumentsModalRef.value) {
    staffDocumentsModalRef.value.fetchDocuments?.()
  }
}

const deleteUser = (user: employee) => {
  selectedEmployeeForDelete.value = user
  isDeleteModalVisible.value = true
}

// Application Details Modal Handlers
const openApplicationDetails = async (emp: employee) => {
  if (!emp.application_id) {
    toast.error('This staff member does not have an associated application record.')
    return
  }
  isLoadingApplicationDetails.value = true
  selectedApplicationDetails.value = null
  isApplicationDetailsModalVisible.value = true
  try {
    const response = await applicationService.getApplication(emp.application_id)
    if (response.success && response.data) {
      selectedApplicationDetails.value = response.data
    } else {
      toast.error('Failed to load application details.')
      isApplicationDetailsModalVisible.value = false
    }
  } catch (err: any) {
    const msg = err.response?.data?.message || err.message || 'Error loading application details.'
    toast.error(msg)
    isApplicationDetailsModalVisible.value = false
  } finally {
    isLoadingApplicationDetails.value = false
  }
}

const handleCloseApplicationDetails = () => {
  isApplicationDetailsModalVisible.value = false
  selectedApplicationDetails.value = null
}

const handleApplicationDocumentsUpdated = () => {
  fetchEmployees()
}

const checkRouteForOpenDocuments = () => {
  if (route.query.open_docs === 'true' && route.query.employee_id) {
    const empId = Number(route.query.employee_id)
    const emp = employees.value.find(e => e.user_details_id === empId || e.user_id === empId)
    if (emp) {
      const initialCat = (route.query.initial_filter as string) || (route.query.filter as string) || 'all'
      openStaffDocuments(emp, initialCat)
    }
  }
}

const checkRouteForOpenApplication = () => {
  if (
    (route.query.view_application === 'true' || route.query.application_id) &&
    (route.query.employee_id || route.query.application_id)
  ) {
    const empId = Number(route.query.employee_id)
    const appId = Number(route.query.application_id)
    const emp = employees.value.find(
      e => (empId && (e.user_details_id === empId || e.user_id === empId)) || (appId && e.application_id === appId)
    )
    if (emp && emp.application_id) {
      openApplicationDetails(emp)
    }
  }
}

watch(
  () => [route.query.open_docs, route.query.view_application, route.query.application_id, route.query.employee_id, employees.value.length],
  () => {
    checkRouteForOpenDocuments()
    checkRouteForOpenApplication()
  }
)

// Load employees on component mount
onMounted(async () => {
  await fetchEmployees()
  checkRouteForOpenDocuments()
  checkRouteForOpenApplication()
})
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

/* Custom focus styles for better accessibility */
.focus\:ring-primary:focus {
  --tw-ring-opacity: 0.5;
  --tw-ring-color: rgb(79 70 229 / var(--tw-ring-opacity));
}

/* Hover effects for interactive elements */
input:hover,
select:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Dark mode ring colors */
.dark .focus\:ring-primary:focus {
  --tw-ring-color: rgb(129 140 248 / var(--tw-ring-opacity));
}
</style>
